/**
 * EditableBlock - 单个可编辑块组件
 * 复刻 trial_demo_editor 的 Block.jsx 渲染结构：
 * outerContainer > contentWrapper > [gutter, selectionWrapper > [bullet, content]]
 */

import React, { useRef, useEffect, useCallback, useState } from 'react';
import { GripVertical, Link as LinkIcon } from 'lucide-react';
import type { EditableBlockProps } from './types';
import { setCursorPosition, getCursorOffset } from './useBlockReducer';
import { getCopiedBlocks } from './BlockEditorContainer';
import { WORD_CONTENT_WIDTH_PX } from './blockStyleUtils';
import { useLanguage } from '../../../i18nContext';

const EditableBlock = React.memo(function EditableBlock({
  block,
  index,
  isActive,
  cursorOffset,
  isSelected,
  hasSelection,
  isDragging,
  isMultiDragOther,
  multiDragCount,
  dragHandleProps,
  dispatch,
  blocks,
  isLinkedActive,
  hasMatch,
  isLineEndpoint,
  onLinkToggle,
  bulletText,
  styleConfig,
}: EditableBlockProps) {
  const { t } = useLanguage();
  const contentRef = useRef<HTMLDivElement>(null);
  const lastContentRef = useRef(block.content);
  const isClickFocusRef = useRef(false);
  const isArrowNavRef = useRef(false);
  const isComposingRef = useRef(false);
  const [hovered, setHovered] = useState(false);

  // ref callback：DOM 元素挂载时立即同步 content
  const setContentRef = useCallback((el: HTMLDivElement | null) => {
    (contentRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
    if (el) {
      const currentText = el.innerText.replace(/\n$/, '');
      if (currentText !== block.content) {
        el.innerText = block.content;
      }
      lastContentRef.current = block.content;
    }
  }, [block.content]);

  // 同步外部 content 变化到 DOM（undo/merge/replace/selection 切换等场景）
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const currentText = el.innerText.replace(/\n$/, '');
    if (currentText !== block.content) {
      el.innerText = block.content;
    }
    lastContentRef.current = block.content;
  }, [block.content, block.id, isDragging, isSelected]);

  // 聚焦管理
  useEffect(() => {
    if (!isActive || isSelected) return;
    const el = contentRef.current;
    if (!el) return;

    const isAlreadyFocused = document.activeElement === el;
    if (!isAlreadyFocused) {
      el.focus({ preventScroll: true });
    }

    // 如果是用户点击聚焦且没有指定光标位置，保持浏览器原生选区不动
    if (isClickFocusRef.current && cursorOffset === null) {
      isClickFocusRef.current = false;
      return;
    }

    // 箭头键导航：由 requestAnimationFrame 自行设置光标，跳过此处
    if (isArrowNavRef.current && cursorOffset === null) {
      isArrowNavRef.current = false;
      return;
    }

    if (!isClickFocusRef.current || cursorOffset !== null) {
      try {
        const range = document.createRange();
        const sel = window.getSelection();
        const textNode = el.firstChild || el;
        if (textNode) {
          const len = textNode.nodeType === 3 ? (textNode as Text).length : 0;
          let offset = 0;
          if (cursorOffset === null || cursorOffset === undefined) {
            offset = len;
          } else {
            offset = Math.min(cursorOffset, len);
          }
          if (textNode.nodeType === 3) {
            range.setStart(textNode, offset);
            range.collapse(true);
          } else {
            range.selectNodeContents(el);
            range.collapse(false);
          }
          sel?.removeAllRanges();
          sel?.addRange(range);
        }
      } catch (_e) { /* ignore */ }
    }

    isClickFocusRef.current = false;
  }, [isActive, isSelected, cursorOffset]);

  // 同步内容到 state
  const syncContent = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    const text = el.innerText.replace(/\n$/, '');
    if (text !== lastContentRef.current) {
      lastContentRef.current = text;
      dispatch({ type: 'UPDATE_CONTENT', id: block.id, content: text });
    }
  }, [dispatch, block.id]);

  const handleInput = useCallback((e: React.FormEvent<HTMLDivElement>) => {
    if (isComposingRef.current) return;
    const el = e.target as HTMLDivElement;
    const text = el.innerText.replace(/\n$/, '');
    lastContentRef.current = text;
    // 获取当前光标位置，传给 reducer 以便 undo 时恢复
    const offset = getCursorOffset(el);
    dispatch({ type: 'UPDATE_CONTENT', id: block.id, content: text, cursorOffset: offset });
  }, [dispatch, block.id]);

  const handleCompositionStart = useCallback(() => {
    isComposingRef.current = true;
  }, []);

  const handleCompositionEnd = useCallback((e: React.CompositionEvent<HTMLDivElement>) => {
    isComposingRef.current = false;
    const el = e.target as HTMLDivElement;
    const text = el.innerText.replace(/\n$/, '');
    lastContentRef.current = text;
    const offset = getCursorOffset(el);
    dispatch({ type: 'UPDATE_CONTENT', id: block.id, content: text, cursorOffset: offset });
  }, [dispatch, block.id]);

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    // 检查是否有结构化 block 数据
    const copiedBlocks = getCopiedBlocks();
    const plainText = e.clipboardData.getData('text/plain');
    if (copiedBlocks && copiedBlocks.length > 1) {
      // 验证剪贴板内容包含 copiedBlocks 的文本（宽松匹配）
      const copiedTexts = copiedBlocks.map(b => b.content);
      const allPresent = copiedTexts.every(t => plainText.includes(t));
      if (allPresent) {
        const el = contentRef.current;
        const offset = el ? getCursorOffset(el) : 0;
        dispatch({
          type: 'PASTE_BLOCKS',
          targetId: block.id,
          cursorOffset: offset,
          pasteBlocks: copiedBlocks as any,
        });
        return;
      }
    }
    // 普通纯文本粘贴
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    range.deleteContents();
    const textNode = document.createTextNode(plainText);
    range.insertNode(textNode);
    range.setStartAfter(textNode);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
    const el = contentRef.current;
    if (el) {
      el.normalize(); // 合并相邻文本节点，确保光标偏移计算正确
      const newText = el.innerText.replace(/\n$/, '');
      lastContentRef.current = newText;
      const cursorPos = getCursorOffset(el);
      dispatch({ type: 'UPDATE_CONTENT', id: block.id, content: newText, cursorOffset: cursorPos });
    }
  }, [dispatch, block.id]);

  const handleBlur = useCallback(() => {
    syncContent();
  }, [syncContent]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (contentRef.current && contentRef.current.contains(e.target as Node)) {
      isClickFocusRef.current = true;
    } else {
      isClickFocusRef.current = false;
    }
  }, []);

  const handleClick = useCallback((e: React.MouseEvent) => {
    // Ctrl+Click: 切换选中
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      dispatch({ type: 'TOGGLE_SELECTION', id: block.id });
      return;
    }
    // Shift+Click: 范围选中
    if (e.shiftKey) {
      e.preventDefault();
      dispatch({ type: 'RANGE_SELECTION', id: block.id });
      return;
    }
    // 普通点击：清除多选，设置活跃
    if (isSelected) {
      dispatch({ type: 'SET_SELECTION', ids: [] });
    }
    isClickFocusRef.current = true;
    dispatch({ type: 'SET_ACTIVE', id: block.id });
  }, [dispatch, block.id, isSelected]);

  const handleGutterClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch({ type: 'TOGGLE_SELECTION', id: block.id });
  }, [dispatch, block.id]);

  // ============ 键盘处理 ============
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (isSelected) return;
    if (isComposingRef.current) return;
    const el = contentRef.current;
    if (!el) return;

    const rawText = el.innerText;
    const safeText = rawText.replace(/\n$/, '');
    const len = safeText.length;
    const sel = window.getSelection();
    const offset = sel?.anchorOffset ?? 0;

    // Enter: 拆分 block
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();

      let prefix: string, suffix: string;
      if (sel && !sel.isCollapsed) {
        const start = Math.min(sel.anchorOffset, sel.focusOffset);
        const end = Math.max(sel.anchorOffset, sel.focusOffset);
        prefix = safeText.slice(0, start);
        suffix = safeText.slice(end);
      } else {
        prefix = safeText.slice(0, offset);
        suffix = safeText.slice(offset);
      }

      lastContentRef.current = prefix;
      el.innerText = prefix;
      dispatch({ type: 'UPDATE_CONTENT', id: block.id, content: prefix });
      dispatch({
        type: 'ADD',
        afterId: block.id,
        content: suffix,
        level: block.level,
        focusNew: true,
      });
      return;
    }

    // Backspace 在行首
    if (e.key === 'Backspace') {
      const isAtStart = sel?.isCollapsed && offset === 0;
      if (isAtStart) {
        if (index === 0) return;
        e.preventDefault();
        const prevBlock = blocks[index - 1];
        if (!safeText) {
          dispatch({ type: 'DELETE', ids: [block.id] });
        } else {
          const mergedContent = prevBlock.content + safeText;
          const cursorPos = prevBlock.content.length;
          dispatch({
            type: 'MERGE',
            destId: prevBlock.id,
            srcId: block.id,
            mergedContent,
            cursorOffset: cursorPos,
          });
        }
        return;
      }
    }

    // Delete 在行尾
    if (e.key === 'Delete') {
      const isAtEnd = sel?.isCollapsed && offset >= len;
      if (isAtEnd) {
        if (index === blocks.length - 1) return;
        e.preventDefault();
        const nextBlock = blocks[index + 1];
        if (!nextBlock.content) {
          dispatch({ type: 'DELETE', ids: [nextBlock.id] });
        } else {
          const mergedContent = safeText + nextBlock.content;
          dispatch({
            type: 'MERGE',
            destId: block.id,
            srcId: nextBlock.id,
            mergedContent,
            cursorOffset: offset,
          });
        }
        return;
      }
    }

    // Tab: 改变层级（有框选时跳过，由 BlockEditorContainer 处理）
    if (e.key === 'Tab' && !hasSelection) {
      e.preventDefault();
      const newLevel = e.shiftKey
        ? Math.max(1, block.level - 1)
        : Math.min(5, block.level + 1);
      if (newLevel !== block.level) {
        dispatch({ type: 'UPDATE_LEVEL', id: block.id, level: newLevel });
      }
      return;
    }

    // ArrowUp: 只在光标处于第一行时跳到上一个 block（保持水平位置）
    if (e.key === 'ArrowUp') {
      if (index === 0) return;
      // 检测光标是否在第一行：比较光标 rect 与元素第一行 rect 的 top
      const selObj = window.getSelection();
      if (!selObj || selObj.rangeCount === 0) return;
      const cursorRange = selObj.getRangeAt(0);
      const cursorRect = cursorRange.getBoundingClientRect();
      // 获取元素第一行的 top（用一个 range 选中第一个字符）
      const firstLineTop = el.getBoundingClientRect().top;
      // 光标在第一行：cursorRect.top 接近元素顶部（容差 2px）
      if (cursorRect.top - firstLineTop > 2) {
        // 不在第一行，让浏览器原生处理
        return;
      }
      e.preventDefault();
      syncContent();
      // 保持水平位置：记住当前光标的 x 坐标，在上一个 block 的最后一行找到对应位置
      const cursorX = cursorRect.left;
      isArrowNavRef.current = true;
      dispatch({ type: 'SET_ACTIVE', id: blocks[index - 1].id, cursorOffset: null });
      // 延迟设置光标位置（等 DOM 更新后）
      requestAnimationFrame(() => {
        const prevBlockEl = document.querySelector(`[data-block-id="${blocks[index - 1].id}"] [contenteditable]`) as HTMLElement;
        if (!prevBlockEl) return;
        // 用 caretPositionFromPoint / caretRangeFromPoint 找到对应 x 位置
        const prevRect = prevBlockEl.getBoundingClientRect();
        const targetY = prevRect.bottom - 2; // 最后一行
        let caretRange: Range | null = null;
        if (document.caretRangeFromPoint) {
          caretRange = document.caretRangeFromPoint(cursorX, targetY);
        }
        if (caretRange && prevBlockEl.contains(caretRange.startContainer)) {
          const s = window.getSelection();
          s?.removeAllRanges();
          s?.addRange(caretRange);
        } else {
          // fallback: 光标放到末尾
          setCursorPosition(prevBlockEl, prevBlockEl.textContent?.length ?? 0);
        }
      });
      return;
    }

    // ArrowDown: 只在光标处于最后一行时跳到下一个 block（保持水平位置）
    if (e.key === 'ArrowDown') {
      if (index === blocks.length - 1) return;
      const selObj = window.getSelection();
      if (!selObj || selObj.rangeCount === 0) return;
      const cursorRange = selObj.getRangeAt(0);
      const cursorRect = cursorRange.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      // 光标在最后一行：cursorRect.bottom 接近元素底部（容差 2px）
      if (elRect.bottom - cursorRect.bottom > 2) {
        return;
      }
      e.preventDefault();
      syncContent();
      const cursorX = cursorRect.left;
      isArrowNavRef.current = true;
      dispatch({ type: 'SET_ACTIVE', id: blocks[index + 1].id, cursorOffset: null });
      requestAnimationFrame(() => {
        const nextBlockEl = document.querySelector(`[data-block-id="${blocks[index + 1].id}"] [contenteditable]`) as HTMLElement;
        if (!nextBlockEl) return;
        const nextRect = nextBlockEl.getBoundingClientRect();
        const targetY = nextRect.top + 2; // 第一行
        let caretRange: Range | null = null;
        if (document.caretRangeFromPoint) {
          caretRange = document.caretRangeFromPoint(cursorX, targetY);
        }
        if (caretRange && nextBlockEl.contains(caretRange.startContainer)) {
          const s = window.getSelection();
          s?.removeAllRanges();
          s?.addRange(caretRange);
        } else {
          setCursorPosition(nextBlockEl, 0);
        }
      });
      return;
    }

    // ArrowLeft 在行首
    if (e.key === 'ArrowLeft' && sel?.isCollapsed && offset === 0) {
      if (index === 0) return;
      e.preventDefault();
      syncContent();
      dispatch({ type: 'SET_ACTIVE', id: blocks[index - 1].id, cursorOffset: blocks[index - 1].content.length });
      return;
    }

    // ArrowRight 在行尾
    if (e.key === 'ArrowRight' && sel?.isCollapsed && offset >= len) {
      if (index === blocks.length - 1) return;
      e.preventDefault();
      syncContent();
      dispatch({ type: 'SET_ACTIVE', id: blocks[index + 1].id, cursorOffset: 0 });
      return;
    }
  }, [block, blocks, index, dispatch, syncContent, isSelected, hasSelection]);

  // ============ 渲染 - 拖拽按钮在外层，内容区与 Word 一致 ============
  return (
    <div
      className={`group${isSelected ? ' is-selected-block' : ''}`}
      style={{
        display: 'flex',
        alignItems: 'stretch',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      data-block-id={block.id}
    >
      {/* 拖拽手柄 - 固定在左侧，与内容第一行对齐 */}
      <div
        style={{
          width: '24px',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          cursor: 'grab',
          userSelect: 'none',
          paddingTop: styleConfig.outerContainer.marginTop || '0px',
          position: 'relative',
        }}
        className={`transition-all ${hovered || isSelected ? 'opacity-100' : 'opacity-0 hover:opacity-100'}`}
        onClick={handleGutterClick}
        {...dragHandleProps}
      >
        <GripVertical size={14} className="text-slate-400 group-hover:text-slate-600" style={{ marginTop: '3px' }} />
        {/* 多选拖动计数 badge */}
        {multiDragCount > 1 && isDragging && (
          <div style={{
            position: 'absolute',
            top: '-2px',
            right: '-4px',
            background: '#2563eb',
            color: '#fff',
            fontSize: '10px',
            fontWeight: 700,
            borderRadius: '50%',
            width: '16px',
            height: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 1,
            zIndex: 20,
          }}>
            {multiDragCount}
          </div>
        )}
      </div>

      {/* 内容区 - 与 Word A4 导出完全一致 */}
      <div style={{ width: `${WORD_CONTENT_WIDTH_PX}px`, flexShrink: 0, minWidth: 0, overflow: 'hidden' }}>
        <div style={styleConfig.outerContainer}>
          <div style={styleConfig.contentWrapper}>
            <div
              data-selection-wrapper={block.id}
              style={{
                ...styleConfig.selectionWrapper,
                backgroundColor: isSelected ? 'rgba(35, 131, 226, 0.25)' : (isLinkedActive ? 'rgba(232, 237, 223, 0.5)' : 'transparent'),
                boxShadow: isSelected ? 'inset 0 0 0 1px rgba(35, 131, 226, 0.35)' : (isLinkedActive ? 'inset 0 0 0 1px #4d7c0f' : undefined),
                borderRadius: (isSelected || isLinkedActive) ? '3px' : undefined,
                padding: isLinkedActive ? '2px 6px' : styleConfig.selectionWrapper?.padding,
                opacity: isSelected && !isDragging ? 0.6 : 1,
                userSelect: isSelected ? 'none' : 'auto',
              }}
            >
              {bulletText && (
                <div style={styleConfig.bullet}>
                  {bulletText}
                </div>
              )}

              <div
                ref={setContentRef}
                contentEditable={!isSelected}
                suppressContentEditableWarning
                data-placeholder={t.workspace.inputPlaceholder}
                onInput={handleInput}
                onPaste={handlePaste}
                onCompositionStart={handleCompositionStart}
                onCompositionEnd={handleCompositionEnd}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                style={styleConfig.content}
              />
            </div>
          </div>
        </div>
      </div>

      {/* 连线按钮 - 固定在右侧，与拖拽手柄对称 */}
      <div
        style={{
          width: '24px',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          paddingTop: styleConfig.outerContainer.marginTop || '0px',
        }}
      >
        {hasMatch && (!isLinkedActive || isLineEndpoint) && onLinkToggle && (
          <button
            data-link-btn={block.id}
            onClick={(e) => {
              e.stopPropagation();
              onLinkToggle(block.id);
            }}
            style={{
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: isLinkedActive ? '2px solid white' : '2px solid #E8EDDF',
              background: isLinkedActive ? '#1a2e05' : 'white',
              color: isLinkedActive ? 'white' : '#a89f91',
              cursor: 'pointer',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              padding: 0,
              marginTop: '3px',
              opacity: isLinkedActive ? 1 : (hovered ? 1 : 0),
              transition: 'opacity 0.15s, background 0.15s',
            }}
            title={t.workspace.viewLinkedSource}
          >
            <LinkIcon size={10} strokeWidth={2.5} />
          </button>
        )}
      </div>
    </div>
  );
});

export default EditableBlock;
